import React, { useState, useEffect } from "react";
import "../../assets/css/members.css";
import RowContainer from "../../Components/Common/RowContainer";
import Container from "../../Components/Common/Container";
import Label from "../../Components/Common/Label";
import ColumnContainer from "../../Components/Common/ColumnContainer";
import { useNavigate, useSearchParams } from "react-router-dom";
import styled from "styled-components";
import CustomDropdown from "../../Components/Common/CustomDropdown";
import formatDate from "../../Components/DateFormat";
import VerticalSpace from "../../Components/VerticalSpace";
import DragDropWord from "../../Components/DragDropWords/DragDropWord";
import "../../stylesheets/indexpage.css";
import PaginationComponent from "../../Components/Common/PaginationComponent";
import { baseURL, getRequest, postRequest } from "../../api/apiClient";
import Toast from "../../Components/Common/Toast";
import DeleteConfirmation from "../../Components/Common/DeleteConfirmation";
import { setDropDown } from "../../redux/lessonsSlice";
import { setParticles } from "../../redux/particlesReducerSlice";
import { useDispatch } from "react-redux";
import Loader from "../../utils/Loading";
import gsap from "gsap";
import "../../stylesheets/common.css";
import ShimmerEffect from "../../utils/ShimmerEffect";
import { useResponsiveFlex } from "../../utils/useResponsiveFlex";
import { useDebounce } from "../../hooks/useDebounce";
import copyIcon from "../../assets/copyIcon.svg";

const InnerRowContainer = styled.div`
  display: flex;
  width: 100%;
  min-width: 1100px;
`;

const ScrollableRowContainer = styled(RowContainer)`
  @media (max-width: 1150px) {
    width: 100%;
    overflow-x: auto;
  }
`;

interface DragDropWordsList {
  id: number;
  sentence: string;
  themeName: string;
  themeIsDeleted: boolean;
  lessonLevel: string;
  points: string;
  updatedAt: string;
  active: boolean;
  include?: boolean;
}

const DragDropWordsList = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [dragDropWords, setDragDropWords] = useState<DragDropWordsList[]>([]);
  const [selectedItem, setSelectedItem] = useState<string | null>(searchParams.get("filter") || null);
  const [selectedLevel, setSelectedLevel] = useState<string | null>(searchParams.get("level") || null);
  const [searchQuery, setSearchQuery] = useState(searchParams.get("search") || "");
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedWords, setSelectedWords] = useState<number[]>([]);
  const [selectedWordsId, setSelectedWordsId] = useState<number | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [filterLoading, setFilterLoading] = useState(false);
  const [page, setPage] = useState(parseInt(searchParams.get("page") || "0"));
  const [count, setCount] = useState(0);
  const [limit, setLimit] = useState(parseInt(searchParams.get("limit") || "5"));
  const [hasNextPage, setHasNextPage] = useState(false);
  const [hasPreviousPage, setHasPreviousPage] = useState(false);
  const flex = useResponsiveFlex();

  const getFiltersFromItem = (item: string | null): { level?: string; updatedAt?: number; active?: string } => {
    switch (item) {
      case "Latest update": return { updatedAt: -1 };
      case "Active": return { active: "true" };
      case "InActive": return { active: "false" };
      default: return {};
    }
  };

  const buildParams = (p: number, l: number, s: string, f: string | null, lv: string | null): Record<string, string> => {
    const params: Record<string, string> = { page: p.toString(), limit: l.toString() };
    if (s) params.search = s;
    if (f && f !== "All") params.filter = f;
    if (lv && lv !== "All Levels") params.level = lv;
    return params;
  };

  const [filters, setFilters] = useState<{
    level?: string;
    updatedAt?: number;
    active?: string;
  }>(() => {
    const init = getFiltersFromItem(searchParams.get("filter"));
    const urlLevel = searchParams.get("level");
    if (urlLevel && urlLevel !== "All Levels") init.level = urlLevel;
    return init;
  });

  const handleSelect = (item: string) => {
    setSelectedItem(item);
  };

  const handleLevelSelect = (item: string) => {
    setSelectedLevel(item);
  };

  const dropdownItems: string[] = [
    "All",
    "Active",
    "InActive",
    "Latest update",
  ];

  const levelDropdownItems: string[] = [
    "All Levels",
    "A1",
    "A2",
    "B1",
    "B2",
  ];

  const debouncedSearch = useDebounce(searchQuery, 500);

  // Sync state when browser Back/Forward changes the URL
  useEffect(() => {
    const urlPage = parseInt(searchParams.get("page") || "0");
    const urlLimit = parseInt(searchParams.get("limit") || "5");
    const urlSearch = searchParams.get("search") || "";
    const urlFilter = searchParams.get("filter") || null;
    const urlLevel = searchParams.get("level") || null;
    setPage(urlPage);
    setLimit(urlLimit);
    setSearchQuery(urlSearch);
    setSelectedItem(urlFilter);
    setSelectedLevel(urlLevel);
    const synced = getFiltersFromItem(urlFilter);
    if (urlLevel && urlLevel !== "All Levels") synced.level = urlLevel;
    setFilters(synced);
  }, [searchParams]);

  useEffect(() => {
    const updatedFilters = {
      ...filters,
      search: debouncedSearch,
    };
    fetchWords(page + 1, limit, updatedFilters);
  }, [debouncedSearch, page, limit, filters]);

  //fetch Words list
  const fetchWords = async (
    page: number,
    perPage: number,
    filters: {
      level?: string;
      updatedAt?: number;
      active?: string;
      search?: string;
    }
  ) => {
    if (!filters) {
      setLoading(true);
    } else {
      setFilterLoading(true);
    }
    try {
      let url = `${baseURL}/drag-and-drop-words-challenge/get-all-drag-and-drop-words-challenge`;
      const body = {
        page,
        limit: perPage,
        ...filters,
      };
      const response = await postRequest(url, body);
      setLoading(false);
      setFilterLoading(false);
      if (response?.data) {
        setDragDropWords(response.data.data.result);
        setCount(response.data.data.totalChallengesCount);
        setPage(response.data.data.page - 1);
        setHasNextPage(response.data.data.hasNextPage);
        setHasPreviousPage(response.data.data.hasPreviousPage);
      }

      const fetchLessons = await getRequest(
        `${baseURL}/notification-challenge/get-lesson-dropdown`
      );

      const fetchParticles = await getRequest(
        `${baseURL}/particle/get-dropdown`
      );
      dispatch(setDropDown(fetchLessons?.data?.data || []));
      dispatch(setParticles(fetchParticles?.data?.data || []));
    } catch (error) {
      console.error("Error fetching lessons:", error);
    }
  };

  const handleSearchChange = (query: string) => {
    setSearchQuery(query);
    setPage(0);
    setSearchParams(buildParams(0, limit, query, selectedItem, selectedLevel));
  };

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage);
    setSearchParams(buildParams(newPage, limit, searchQuery, selectedItem, selectedLevel));
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const newLimit = parseInt(event.target.value, 10);
    setLimit(newLimit);
    setPage(0);
    setSearchParams(buildParams(0, newLimit, searchQuery, selectedItem, selectedLevel));
  };

  const handleApplyFilters = () => {
    const newFilters = getFiltersFromItem(selectedItem);
    if (selectedLevel && selectedLevel !== "All Levels") {
      newFilters.level = selectedLevel;
    }
    setFilters(newFilters);
    setPage(0);
    setSearchParams(buildParams(0, limit, searchQuery, selectedItem, selectedLevel));
  };

  const handleEditWords = (id: number) => {
    navigate(`/drag-drop-words/edit-drag-drop-words/${id}`, {
      state: { listPage: page, listLimit: limit, listSearch: searchQuery, listFilter: selectedItem, listLevel: selectedLevel },
    });
  };

  const handleCopyDragDropWord = async (id: number) => {
    try {
      const response = await postRequest(
        `${baseURL}/drag-and-drop-words-challenge/clone-drag-and-drop-words-challenge`,
        { id }
      );
      if (response?.data.success) {
        Toast(
          response.data.statusMessage || "Drag & drop word cloned successfully",
          "success"
        );
        const updatedFilters = { ...filters, search: debouncedSearch };
        fetchWords(page + 1, limit, updatedFilters);
      } else {
        Toast("Failed to clone drag & drop word", "error");
      }
    } catch (error) {
      console.error("Error cloning drag & drop word:", error);
      Toast("Failed to clone drag & drop word", "error");
    }
  };

  /** Bulk copy all selected drag & drop words — clones each one server-side */
  const handleCopySelectedWords = async () => {
    if (selectedWords.length === 0) return;
    try {
      for (const id of selectedWords) {
        await postRequest(
          `${baseURL}/drag-and-drop-words-challenge/clone-drag-and-drop-words-challenge`,
          { id }
        );
      }
      Toast(`${selectedWords.length} drag & drop word(s) copied successfully`, "success");
      setSelectedWords([]);
      const updatedFilters = { ...filters, search: debouncedSearch };
      fetchWords(page + 1, limit, updatedFilters);
    } catch (error) {
      console.error("Error copying drag & drop words:", error);
      Toast("Failed to copy drag & drop words", "error");
    }
  };

  const handleCreateDragDropWords = () => {
    navigate("/drag-drop-words/create-drag-drop-words");
  };

  const handleStatusChange = async (id: number, active: boolean) => {
    const reqData = {
      modelName: "dragAndDropWordsChallenge",
      id: id,
      isActive: !active,
    };
    await postRequest(`${baseURL}/change-status`, reqData);
    // await fetchWords(page + 1, limit, filters);
  };

  const handleIncludeChange = async (id: number, include: boolean) => {
    const reqData = {
      type: "drag-drop-words",
      id: id,
      include: include,
    };
    await postRequest(`${baseURL}/challenge/include/change`, reqData);
  };

  const onCheckboxChange = (id: number) => {
    if (selectedWords.includes(id)) {
      setSelectedWords(selectedWords.filter((selectedId) => selectedId !== id));
    } else {
      setSelectedWords([...selectedWords, id]);
    }
  };

  const deleteSelectedWords = () => {
    if (selectedWords.length > 0) {
      setShowDeleteDialog(true);
    } else {
      Toast("Please select at least one challenge to delete.", "error");
    }
  };

  const deleteWords = async (wordsIds: number | number[]) => {
    try {
      const payload = {
        id: Array.isArray(wordsIds) ? wordsIds : wordsIds,
      };
      const response = await postRequest(
        `${baseURL}/drag-and-drop-words-challenge/delete-drag-and-drop-words-challenge-by-id`,
        payload
      );

      if (response?.status === 200) {
        Toast("Words deleted successfully", "success");
        setDragDropWords((prevData) =>
          prevData.filter((words) =>
            Array.isArray(wordsIds)
              ? !wordsIds.includes(words.id)
              : words.id !== wordsIds
          )
        );
      } else {
        Toast("Failed to delete challenge", "error");
      }
    } catch (error) {
      console.error("Error deleting challenge:", error);
    }
  };

  const handleDelete = async () => {
    if (selectedWordsId !== null) {
      await deleteWords(selectedWordsId);
    } else {
      await deleteWords(selectedWords);
    }
    // await fetchWords(page + 1, limit, filters);
    setSelectedWords([]);
    setSelectedWordsId(null);
    setShowDeleteDialog(false);
  };

  const toggleDeleteDialog = () => {
    setShowDeleteDialog(!showDeleteDialog);
  };

  useEffect(() => {
    let timeLine = gsap.timeline();
    timeLine.from(".row-container", {
      opacity: 0,
      y: -40,
      duration: 0.3,
      ease: "power2.out",
    });

    timeLine.from(".user-data", {
      opacity: 0,
      y: 40,
      duration: 0.5,
      ease: "power2.out",
      stagger: 0.2,
    });
  }, [loading]);

  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <>
          <div className="container-fluid">
            <div className="p-3">
              <div className="row mb-3 ">
                <div className="col">
                  <h2 style={{ fontWeight: "bold" }}>
                    Challenge: drag & drop words
                  </h2>
                </div>
              </div>

              {/* Filter Label */}
              <div className="row mb-3">
                <div className="col">
                  <label htmlFor="filter" className="form-label">
                    Filter
                  </label>
                </div>
              </div>

              {/* Buttons Row */}
              <div className="row mb-3 all-button-responsive">
                <div className="col-3 search-input-div">
                  <input
                    type="search"
                    className="form-control rounded"
                    placeholder="Search"
                    value={searchQuery}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    aria-label="Search"
                    aria-describedby="search-addon"
                    style={{
                      height: "100%",
                      boxShadow: "none",
                    }}
                  />
                </div>
                <div className="col d-flex responsive">
                  <CustomDropdown
                    items={dropdownItems}
                    onSelect={handleSelect}
                    selectedItem={selectedItem || ""}
                    height="50px"
                  />
                  <div className="ms-3">
                    <CustomDropdown
                      items={levelDropdownItems}
                      onSelect={handleLevelSelect}
                      selectedItem={selectedLevel || ""}
                      height="50px"
                    />
                  </div>
                  <button
                    type="button"
                    className="btn btn-primary ms-3 user"
                    style={{
                      backgroundColor: "#100F15",
                      borderRadius: "10px",
                      color: "#FFFFFF",
                      fontSize: "14px",
                      width: "auto",
                      padding: "14px 50px",
                    }}
                    onClick={handleApplyFilters}
                  >
                    Apply
                  </button>
                </div>
                <div className="col d-flex justify-content-end responsive">
                  {/* <button
                    type="button"
                    className="btn btn-outline-secondary me-2 text-center"
                    style={{
                      color: "#100F15",
                      background: "none",
                      border: "1px solid #100F15",
                      borderRadius: "10px",
                      fontSize: "14px",
                      width: "auto",
                      padding: "14px 40px",
                    }}
                    onClick={deleteSelectedWords}
                  >
                    Delete all
                  </button> */}
                  <button
                    type="button"
                    className="btn btn-success user"
                    style={{
                      backgroundColor: "#FF9E00",
                      borderRadius: "10px",
                      color: "#100F15",
                      fontSize: "14px",
                      width: "auto",
                      padding: "14px 40px",
                    }}
                    onClick={handleCreateDragDropWords}
                  >
                    Add New
                  </button>
                </div>
              </div>

              <ScrollableRowContainer>
                <InnerRowContainer>
                  <Container width="100%">
                    <RowContainer
                      alignItem="center"
                      padding="8px 12px 8px 12px"
                      gap="8px"
                      justifyContent="space-between"
                      className="row-container"
                    >
                      {/* Select-all checkbox for current page */}
                      <RowContainer flex="1.4" alignItem="center">
                        <input
                          type="checkbox"
                          title="Select all on this page"
                          checked={
                            dragDropWords.length > 0 &&
                            dragDropWords.every((w) =>
                              selectedWords.includes(w.id)
                            )
                          }
                          ref={(el) => {
                            if (el) {
                              el.indeterminate =
                                selectedWords.length > 0 &&
                                !dragDropWords.every((w) =>
                                  selectedWords.includes(w.id)
                                );
                            }
                          }}
                          onChange={(e) => {
                            if (e.target.checked) {
                              const currentPageIds = dragDropWords.map((w) => w.id);
                              setSelectedWords((prev) => [
                                ...prev.filter((id) => !currentPageIds.includes(id)),
                                ...currentPageIds,
                              ]);
                            } else {
                              const currentPageIds = dragDropWords.map((w) => w.id);
                              setSelectedWords((prev) =>
                                prev.filter((id) => !currentPageIds.includes(id))
                              );
                            }
                          }}
                          style={{
                            width: 16,
                            height: 16,
                            accentColor: "#7542E4",
                            cursor: "pointer",
                          }}
                        />
                      </RowContainer>
                      <RowContainer flex={flex.name}>
                        <Label
                          fontWeight={400}
                          fontSize="14px"
                          color="rgba(164, 163, 167, 1)"
                        >
                          Name
                        </Label>
                      </RowContainer>
                      <RowContainer flex={flex.theme}>
                        <Label
                          fontWeight={400}
                          fontSize="14px"
                          color="rgba(164, 163, 167, 1)"
                        >
                          Theme
                        </Label>
                      </RowContainer>
                      <RowContainer flex="3.1">
                        <Label
                          fontWeight={400}
                          fontSize="14px"
                          color="rgba(164, 163, 167, 1)"
                        >
                          Level
                        </Label>
                      </RowContainer>
                      <RowContainer flex={flex.point}>
                        <Label
                          fontWeight={400}
                          fontSize="14px"
                          color="rgba(164, 163, 167, 1)"
                        >
                          Point
                        </Label>
                      </RowContainer>
                      <RowContainer flex="5">
                        <Label
                          fontWeight={400}
                          fontSize="14px"
                          color="rgba(164, 163, 167, 1)"
                        >
                          Latest updated
                        </Label>
                      </RowContainer>
                      <RowContainer flex="2.3" alignItem="center">
                        <Label
                          fontWeight={400}
                          fontSize="14px"
                          color="rgba(164, 163, 167, 1)"
                        >
                          Include
                        </Label>
                      </RowContainer>
                      <RowContainer flex={flex.action} justifyContent="space-between" alignItem="center">
                        <Label
                          fontWeight={400}
                          fontSize="14px"
                          color="rgba(164, 163, 167, 1)"
                        >
                          Action
                        </Label>
                        {/* Bulk Copy toolbar — visible only when words are selected */}
                        {selectedWords.length > 0 && (
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <button
                              type="button"
                              className="btn user"
                              title={`Copy ${selectedWords.length} selected word(s)`}
                              style={{
                                backgroundColor: "#DAD7FE",
                                color: "#000",
                                borderRadius: 8,
                                fontSize: 12,
                                padding: "5px 12px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: 5,
                                whiteSpace: "nowrap",
                              }}
                              onClick={handleCopySelectedWords}
                            >
                              <img
                                src={copyIcon}
                                alt="copy"
                                style={{ width: 16, height: 16 }}
                              />
                              Copy ({selectedWords.length})
                            </button>
                            <button
                              type="button"
                              className="btn user"
                              title="Clear selection"
                              style={{
                                background: "none",
                                border: "1px solid #aaa",
                                borderRadius: 8,
                                fontSize: 12,
                                padding: "5px 10px",
                                color: "#555",
                                whiteSpace: "nowrap",
                              }}
                              onClick={() => setSelectedWords([])}
                            >
                              ✕ Clear
                            </button>
                          </div>
                        )}
                      </RowContainer>
                    </RowContainer>
                    {filterLoading ? (
                      <>
                        <RowContainer
                          bgColor="#ffffff"
                          borderRadius="8px"
                          padding="10px 0px"
                        >
                          {Array.from({ length: 6 }, (_, index) => {
                            return (
                              <RowContainer
                                key={index}
                                width={
                                  index === 0 || index === 5 ? "10%" : "20%"
                                }
                                justifyContent="center"
                              >
                                <ShimmerEffect />
                              </RowContainer>
                            );
                          })}
                        </RowContainer>
                      </>
                    ) : (
                      <>
                        {dragDropWords && dragDropWords.length > 0 ? (
                          <ColumnContainer>
                            {dragDropWords.map((dragDropWord) => (
                              <DragDropWord
                                key={dragDropWord.id}
                                id={dragDropWord.id}
                                sentence={dragDropWord.sentence || 'N/A'}
                                theme={dragDropWord.themeName}
                                themeIsDeleted={dragDropWord.themeIsDeleted}
                                lessonLevel={dragDropWord.lessonLevel}
                                point={`${dragDropWord.points} pts`}
                                created={formatDate(dragDropWord.updatedAt)}
                                onEdit={handleEditWords}
                                onDelete={() => {
                                  setSelectedWordsId(dragDropWord.id);
                                  toggleDeleteDialog();
                                }}
                                isChecked={selectedWords.includes(
                                  dragDropWord.id
                                )}
                                onCheckboxChange={onCheckboxChange}
                                status={dragDropWord.active}
                                onStatusChange={() =>
                                  handleStatusChange(
                                    dragDropWord.id,
                                    dragDropWord.active
                                  )
                                }
                                include={dragDropWord.include ?? dragDropWord.active}
                                onIncludeChange={() =>
                                  handleIncludeChange(
                                    dragDropWord.id,
                                    !(dragDropWord.include ?? dragDropWord.active)
                                  )
                                }
                                onCopy={handleCopyDragDropWord}
                              />
                            ))}
                          </ColumnContainer>
                        ) : (
                          <>
                            <VerticalSpace height={30} />
                            <Label
                              alignItems="center"
                              display="flex"
                              color="rgba(164, 163, 167, 1)"
                              justifyContent="center"
                            >
                              No results found.
                            </Label>
                          </>
                        )}
                      </>
                    )}
                  </Container>
                </InnerRowContainer>
              </ScrollableRowContainer>
              <VerticalSpace height={10} />
              <RowContainer justifyContent="flex-end" className="user-data">
                <PaginationComponent
                  page={page}
                  rowsPerPage={limit}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  count={count}
                  hasNextPage={hasNextPage}
                  hasPreviousPage={hasPreviousPage}
                />
              </RowContainer>
            </div>
            {showDeleteDialog && (
              <DeleteConfirmation
                showDeletePopup={true}
                setShowDeletePopup={setShowDeleteDialog}
                message={`Are you sure you want to delete ${selectedWords.length === 0
                  ? "this drag & drop word?"
                  : selectedWords.length === 1
                    ? "this drag & drop words?"
                    : "these drag & drop words?"
                  }`}
                deleteButtonLabel="Delete"
                onDelete={handleDelete}
              />
            )}
            <VerticalSpace height={100} />
          </div>
        </>
      )}
    </>
  );
};

export default DragDropWordsList;
